import React, { useEffect, useState } from "react";
import { Compass, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import Modal, { ModalHeader } from "../Modal";
import { api, errMsg } from "../../lib/api";
import { stakeholderNames } from "../StakeholderPicker";
import TemplateStep from "./TemplateStep";
import RolesStep from "./RolesStep";
import PeopleStep from "./PeopleStep";
import DetailsStep from "./DetailsStep";

const STEPS = ["Start from", "Roles", "Teammates & stakeholders", "Project details"];
const EMPTY = { name: "", description: "", program_id: "", pm_teammate_id: "", project_owner_teammate_id: "", stated_due_date: "", stated_budget: 0, time_unit: "Days", stakeholder_ids: [] };

function Stepper({ step }) {
  return (
    <ol className="flex items-center gap-2 mb-5 text-xs" data-testid="wizard-stepper">
      {STEPS.map((s, i) => (
        <li key={s} className="flex items-center gap-2 flex-1 min-w-0" data-testid={`wizard-stepper-${i}`}>
          <span className={`w-6 h-6 shrink-0 rounded-full grid place-items-center font-semibold ${i <= step ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-400"}`}>{i + 1}</span>
          <span className={`truncate ${i === step ? "text-white" : "text-slate-500"}`}>{s}</span>
          {i < STEPS.length - 1 && <span className={`h-px flex-1 ${i < step ? "bg-blue-500" : "bg-slate-800"}`} />}
        </li>
      ))}
    </ol>
  );
}

export default function ProjectSetupWizard({ open, onClose, onCreated, title = "New project setup" }) {
  const [step, setStep] = useState(0);
  const [lib, setLib] = useState({ roles: [], teammates: [], stakeholders: [], templates: [], programs: [] });
  const [form, setForm] = useState(EMPTY);
  const [templateId, setTemplateId] = useState("");
  const [busy, setBusy] = useState(false);
  const setter = (k) => (fn) => setLib((l) => ({ ...l, [k]: typeof fn === "function" ? fn(l[k]) : fn }));

  useEffect(() => {
    if (!open) return;
    setStep(0); setForm(EMPTY); setTemplateId("");
    const keys = ["roles", "teammates", "stakeholders", "templates", "programs"];
    Promise.all(keys.map((k) => api.get(`/${k}`).then((r) => r.data).catch(() => [])))
      .then((vals) => setLib(Object.fromEntries(keys.map((k, i) => [k, vals[i]]))));
  }, [open]);

  const pickTemplate = (id) => {
    setTemplateId(id);
    const src = lib.templates.find((t) => t.id === id)?.source_project;
    if (!src) { setForm(EMPTY); return; }
    setForm({
      name: src.name || "", description: src.description || "", program_id: src.program_id || "",
      pm_teammate_id: lib.teammates.some((t) => t.id === src.pm_teammate_id) ? src.pm_teammate_id : "",
      project_owner_teammate_id: lib.teammates.some((t) => t.id === src.project_owner_teammate_id) ? src.project_owner_teammate_id : "",
      stated_due_date: src.stated_due_date || "", stated_budget: src.stated_budget || 0, time_unit: src.time_unit || "Days",
      stakeholder_ids: (src.stakeholder_ids || []).filter((sid) => lib.stakeholders.some((s) => s.id === sid)),
    });
  };

  const create = async () => {
    if (!form.name.trim()) { toast.error("Give the project a name"); return; }
    setBusy(true);
    const data = { ...form, name: form.name.trim(), program_id: form.program_id || null, pm_teammate_id: form.pm_teammate_id || null, project_owner_teammate_id: form.project_owner_teammate_id || null, stated_budget: Number(form.stated_budget || 0), stakeholders: stakeholderNames(lib.stakeholders, form.stakeholder_ids) };
    try {
      const res = templateId ? await api.post(`/projects/from-template/${templateId}`, { data }) : await api.post("/projects", { data });
      onCreated(res.data, { fromTemplate: !!templateId });
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const last = step === STEPS.length - 1;
  return (
    <Modal open={open} onClose={onClose} size="2xl" testId="project-wizard">
      <ModalHeader icon={Compass} title={title} onClose={onClose} testId="wizard-close" />
      <Stepper step={step} />
      {step === 0 && <TemplateStep templates={lib.templates} templateId={templateId} onPick={pickTemplate} />}
      {step === 1 && <RolesStep roles={lib.roles} setRoles={setter("roles")} />}
      {step === 2 && <PeopleStep roles={lib.roles} teammates={lib.teammates} setTeammates={setter("teammates")} stakeholders={lib.stakeholders} setStakeholders={setter("stakeholders")} />}
      {last && <DetailsStep form={form} setForm={setForm} templates={lib.templates} templateId={templateId} onPickTemplate={pickTemplate} programs={lib.programs} stakeholders={lib.stakeholders} teammates={lib.teammates} />}
      <div className="flex items-center justify-between gap-2 mt-6 pt-4 border-t border-slate-800">
        <button type="button" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-back" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white disabled:opacity-30"><ChevronLeft size={14} /> Back</button>
        <div className="flex items-center gap-2">
          {!last && <button type="button" onClick={() => setStep(step + 1)} data-testid="wizard-skip" className="text-sm text-slate-400 hover:text-white px-3 py-2">Skip</button>}
          {!last
            ? <button type="button" onClick={() => setStep(step + 1)} data-testid="wizard-next" className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm">Next <ChevronRight size={14} /></button>
            : <button type="button" disabled={busy || !form.name.trim()} onClick={create} data-testid="wizard-create" className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">{busy ? "Creating…" : templateId ? "Create from template" : "Create project"}</button>}
        </div>
      </div>
    </Modal>
  );
}
